import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Home, Wrench, Tags, User, MessageCircle, ArrowRight, Cog } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { categoriesAPI } from '../services/api';
import { useT } from '../services/i18n';
import { catName } from '../services/catalogI18n';
import { usePageMeta } from '../services/seo';

const SHORTCUTS = [
  { to: '/', label: 'common.home', icon: Home, testId: 'notfound-home' },
  { to: '/pieces', label: 'sm.allParts', icon: Wrench, testId: 'notfound-parts' },
  { to: '/marques', label: 'header.brands', icon: Tags, testId: 'notfound-brands' },
  { to: '/mon-compte', label: 'header.myAccount', icon: User, testId: 'notfound-account' },
  { to: '/contact', label: 'header.contact', icon: MessageCircle, testId: 'notfound-contact' },
];

// 404 page: bold visual, search, shortcuts, popular categories
const NotFoundPage = () => {
  const { t, lang } = useT();
  usePageMeta({ title: t('nf.metaTitle'), noindex: true });
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [categories, setCategories] = useState([]);
  useEffect(() => { categoriesAPI.getAll().then(c => setCategories(c.slice(0, 8))).catch(() => {}); }, []);

  const submit = (e) => { e.preventDefault(); if (q.trim()) navigate(`/recherche?q=${encodeURIComponent(q.trim())}`); };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Header />
      <main className="flex-1" data-testid="not-found-page">
        <section className="relative bg-[#0e0e10] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_1px_1px,#fff_1px,transparent_0)] [background-size:22px_22px]" />
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-orange-500/25 blur-3xl" />
          <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-center relative">
            <div className="animate-[fadeUp_.7s_ease-out_both]">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-orange-400 mb-4">Erreur 404</p>
              <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black leading-none tracking-tight" data-testid="notfound-title">
                <span className="text-white">4</span><span className="text-orange-500">0</span><span className="text-white">4</span>
              </h1>
              <h2 className="text-lg md:text-xl font-semibold mt-5 text-gray-100">{t('nf.title')}</h2>
              <p className="text-gray-400 mt-2 max-w-lg text-sm sm:text-base">{t('nf.text')}</p>
              <form onSubmit={submit} className="mt-8 flex max-w-lg" data-testid="notfound-search-form">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('nf.searchPh')} data-testid="notfound-search-input"
                  className="flex-1 h-12 rounded-l-full bg-white/10 border border-white/15 px-5 text-white placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white/15" />
                <button type="submit" data-testid="notfound-search-submit" className="h-12 px-6 rounded-r-full bg-orange-500 hover:bg-orange-600 font-semibold inline-flex items-center gap-2 transition-colors"><Search size={18} /> <span className="hidden sm:inline">{t('nf.search')}</span></button>
              </form>
              <div className="flex flex-wrap gap-2 mt-6">
                {SHORTCUTS.map(s => (
                  <Link key={s.to} to={s.to} data-testid={s.testId} className="inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-orange-500 border border-white/10 rounded-full px-4 py-2 transition-colors"><s.icon size={15} /> {t(s.label)}</Link>
                ))}
              </div>
            </div>
            <div className="hidden lg:flex justify-center relative" aria-hidden="true">
              <Cog size={260} strokeWidth={0.6} className="text-orange-500/80 animate-[spin_18s_linear_infinite]" />
              <Cog size={140} strokeWidth={0.8} className="text-white/60 absolute -bottom-6 right-10 animate-[spin_12s_linear_infinite_reverse]" />
              <Wrench size={90} strokeWidth={1} className="text-white absolute top-8 left-6 -rotate-12" />
            </div>
          </div>
        </section>
        {categories.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 py-12">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4">{t('nf.topCategories')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="notfound-categories">
              {categories.map(c => (
                <Link key={c.id} to={`/pieces/${c.slug}`} data-testid={`notfound-category-${c.slug}`} className="group bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                  {c.image && <img src={c.image} alt="" className="w-12 h-12 object-contain" />}
                  <span className="text-sm font-medium text-gray-800 flex-1">{catName(c, lang)}</span>
                  <ArrowRight size={16} className="text-gray-300 group-hover:text-orange-500 transition-colors" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default NotFoundPage;
